/**
 * Privacy Policy page - CHAP-135 (BLOCKS-CUTOVER).
 *
 * Markdown-style content inline. Legal review required before
 * production launch - the language here is a defensible v1 baseline
 * that survives an EU-traveling-alum's data inquiry, but the formal
 * Cooley Go review is a Jake-side step.
 *
 * Per `gdpr-ccpa-scope-decision.md`: GDPR-flavored posture for US-only
 * audience.
 */

import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = '2026-05-27';

export default function Privacy() {
  return (
    <article
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: '40px 24px',
        fontFamily: 'system-ui, sans-serif',
        color: '#03244d',
        lineHeight: 1.6,
      }}
    >
      <h1 style={{ fontSize: 32, marginBottom: 8 }}>Privacy Policy</h1>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 32 }}>
        Effective: {EFFECTIVE_DATE} · 1116 LLC dba ChapterBuild
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>Who we are</h2>
      <p>
        ChapterBuild is a multi-tenant SaaS platform built and operated by
        <strong> 1116 LLC</strong> (a Texas limited liability company), doing business
        as ChapterBuild. We help fraternity and sorority chapters plan and execute
        capital projects (new houses, renovations, fundraising campaigns).
      </p>
      <p>
        Privacy contact: <a href="mailto:privacy@chapterbuild.com" style={{ color: '#03244d' }}>privacy@chapterbuild.com</a>.
        For US-based users, this address acts as our Data Protection Officer contact.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>Scope</h2>
      <p>
        This policy covers the chapter-build web application at
        <code> app.chapterbuild.com</code>, the marketing site at
        <code> chapterbuild.com</code>, and any APIs we operate. Our service is
        intended for organizations and individuals located in the United States, but
        we apply GDPR-aligned privacy practices to all users regardless of location
        because chapter alumni may travel or reside in other jurisdictions.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>What we collect</h2>
      <h3 style={{ fontSize: 16, marginTop: 16 }}>From your tenant (chapter)</h3>
      <ul>
        <li>Your name, email address, profile picture (if you provide one)</li>
        <li>Your role and tier within your tenant (Cabinet, Board, Voter, Observer)</li>
        <li>
          For Cabinet members: contributions to your tenant's voting, OKRs,
          feedback, data room uploads, and meeting attendance
        </li>
      </ul>
      <h3 style={{ fontSize: 16, marginTop: 16 }}>About your usage</h3>
      <ul>
        <li>
          Authentication logs (when you sign in, from what IP address - held
          90 days for security and audit)
        </li>
        <li>Access logs for data room documents (who downloaded what, when)</li>
        <li>Error reports (via Sentry; no IPs or user agents are sent)</li>
        <li>
          Aggregate product analytics (via PostHog; OFF by default, only collected
          if you opt in via our cookie banner)
        </li>
      </ul>
      <h3 style={{ fontSize: 16, marginTop: 16 }}>What we do NOT collect</h3>
      <ul>
        <li>Government-issued identification numbers</li>
        <li>Health information</li>
        <li>Browser fingerprints</li>
        <li>Third-party advertising data</li>
        <li>Cross-site tracking data</li>
        <li>Session replay video</li>
      </ul>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>Why we collect it</h2>
      <p>
        Most data is collected because <strong>you or your tenant Cabinet asked us
        to</strong>. You log in to participate in your chapter's capital project work;
        we keep your role and your contributions so the rest of your Cabinet sees
        them. Beyond that:
      </p>
      <ul>
        <li>
          <strong>Authentication logs</strong> - to detect and prevent
          account takeover (legitimate interest under GDPR Art. 6(1)(f))
        </li>
        <li>
          <strong>Error reports</strong> - to fix bugs that affect your
          experience (legitimate interest; minimal data - no IPs, no
          user agents, no PII in error messages)
        </li>
        <li>
          <strong>Analytics</strong> - to understand how the dashboard is used,
          only if you opt in (consent under GDPR Art. 6(1)(a))
        </li>
        <li>
          <strong>Audit logs of sensitive document access</strong> - required for
          security and compliance with our tenant DPA commitments (legal obligation
          and contractual necessity)
        </li>
      </ul>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>Who we share data with</h2>
      <p>
        We use the following third-party service providers ("subprocessors").
        Each is bound by a data processing agreement with us.
      </p>
      <ul>
        <li>
          <strong>Stripe</strong> (payment processing) - US-based with global
          processing. Stripe sees billing information, never your chapter's
          financial-model data or data room contents.
        </li>
        <li>
          <strong>SendGrid (a Twilio company)</strong> (transactional email) -
          US-based. SendGrid sees the email address we're sending to and the
          message body. We use a dedicated subuser (chapterbuild) so our email
          stream is auditably separate from other 1116 LLC products.
        </li>
        <li>
          <strong>Cloudflare</strong> (DNS, storage via R2) - global edge
          network. Data room files are stored in R2 in our WNAM (Western
          North America) region.
        </li>
        <li>
          <strong>Render</strong> (application hosting) - US-based (Oregon).
          Hosts our web application, databases, and background jobs.
        </li>
        <li>
          <strong>Sentry</strong> (error reporting) - US-based. Sees stack
          traces (with PII scrubbing enabled). Used only for fixing bugs.
        </li>
        <li>
          <strong>PostHog</strong> (product analytics, opt-in only) - US-based
          (when used). Sees aggregate usage patterns. Off by default.
        </li>
      </ul>
      <p>
        We do <strong>not</strong> sell your data, share it for advertising
        purposes, or otherwise disclose it except as required by law or with
        your tenant's explicit consent.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>How long we keep data</h2>
      <ul>
        <li>
          <strong>Account data</strong>: retained for the lifetime of your
          tenant membership, plus 30 days after you leave (to allow for
          accidental-removal recovery)
        </li>
        <li>
          <strong>Authentication logs</strong>: 90 days
        </li>
        <li>
          <strong>Access logs (data room)</strong>: 2 years (compliance + audit
          requirement)
        </li>
        <li>
          <strong>Error reports</strong>: 90 days (Sentry retention default)
        </li>
        <li>
          <strong>Analytics events</strong>: 12 months
        </li>
        <li>
          <strong>Cabinet meeting recordings</strong>: per your tenant's
          governance policy (defaults to 5 years for unprocessed recordings,
          permanent for redacted transcripts)
        </li>
      </ul>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>Your rights</h2>
      <p>You can ask us to:</p>
      <ul>
        <li>Show you the data we hold about you (Right of access)</li>
        <li>Correct anything that's wrong (Right to rectification)</li>
        <li>
          Delete your account and associated data (Right to erasure /
          right to be forgotten - subject to certain audit and legal-hold
          retention requirements)
        </li>
        <li>Pause processing while a question is resolved (Right to restriction)</li>
        <li>Receive your data in a portable format (Right to portability)</li>
        <li>Object to a specific processing activity (Right to object)</li>
      </ul>
      <p>
        To exercise any of these, email <a href="mailto:privacy@chapterbuild.com">privacy@chapterbuild.com</a>.
        We respond within 30 days, often within a few business days.
      </p>
      <p>
        California residents have additional rights under the CCPA / CPRA. You
        can ask us what categories of personal information we've collected,
        what we've used it for, and to whom we've disclosed it. Same email
        address; same 30-day response window.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>Cookies</h2>
      <p>
        We use three categories of cookies. See our{' '}
        <Link to="/legal/cookies" style={{ color: '#03244d' }}>
          cookie preferences page
        </Link>{' '}
        for detail and to change your settings.
      </p>
      <ul>
        <li>
          <strong>Strictly necessary</strong>: keeps you signed in,
          remembers your CSRF token, scopes you to the right tenant. Cannot
          be disabled without breaking sign-in.
        </li>
        <li>
          <strong>Analytics</strong> (off by default): helps us understand
          how the dashboard is used. Opt-in only.
        </li>
        <li>
          <strong>Marketing</strong>: we don't use marketing cookies. This
          category is present for transparency only.
        </li>
      </ul>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>Changes to this policy</h2>
      <p>
        When we make material changes to this policy, we re-prompt you to
        review the cookie banner and email tenant administrators a summary
        of what changed. The effective date at the top of this page always
        reflects the latest revision.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>Contact</h2>
      <p>
        Questions about this policy or our practices:{' '}
        <a href="mailto:privacy@chapterbuild.com" style={{ color: '#03244d' }}>
          privacy@chapterbuild.com
        </a>
        .
      </p>
      <p>
        Postal address:
        <br />
        1116 LLC
        <br />
        Attn: Privacy
        <br />
        [Address TBD - Texas registered agent]
      </p>

      <hr style={{ margin: '32px 0', borderColor: '#e5e7eb' }} />
      <p style={{ color: '#6b7280', fontSize: 14 }}>
        See also our{' '}
        <Link to="/legal/terms" style={{ color: '#03244d' }}>
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link to="/legal/cookies" style={{ color: '#03244d' }}>
          cookie preferences
        </Link>
        .
      </p>
    </article>
  );
}
